import React, { useState, useEffect, useRef } from "react";
import { formatDate } from "../Functions/formatDate";
import { SubscriptionsTable } from "./DevadminOrders";
import Button from "../Button";
import ResenMailPopup from "../Popups/ResenMailPopup";
import PopupModal from "./popups/PopupModal";
import apiInstance from "../apiInstance";
import { PaymentTable } from "./DevadminPayments";

const UserDetails = ({
  users,
  closeUserDetail,
  switchUser,
  updateUser,
  deleteUserMain,
}) => {
  // Use local state to track the current user data
  const [userData, setUserData] = useState(users);
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messagesShowing, setMessagesShowing] = useState(false);
  const popupModalRef = useRef();

  // Update local user data when the prop changes
  useEffect(() => {
    setUserData(users);
  }, [users]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      setLoading(true);
      try {
        const response = await apiInstance.get("devadmin/details/"+userData.id);
        setSubscriptions(response.data.user.widgetArray);
        setPayments(response.data.user.payments);
        setPaymentMethods(response.data.user.paymentMethods);
      } catch (error) {
        console.error("Error fetching user details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUserDetails();
  }, [userData.id]);

  const handleEmailVerification = (email) => {
    const requestBody = { email };

    apiInstance
      .post("/user/verifyEmailRequest", requestBody)
      .then(() => {
        setMessagesShowing(true);

        setTimeout(() => {
          setMessagesShowing(false);
        }, 5000);
      })
      .catch((error) => {
        console.log("Error for Verification Mail : " + error.message);
      });
  };

  const handleDeleteUser = async () => {
    if (userData && userData.id) {
      await popupModalRef.current.openPopup("deleteUser", {
        userId: userData.id,
        closeUserDetail: closeUserDetail,
      });
    }
  };

  const handleEditUser = () => {
    if (userData) {
      popupModalRef.current.openPopup("updateUser", { user: userData });
    }
  };

  // Custom handler for user update to refresh local state
  const handleUserUpdate = (updatedUser) => {
    setUserData(updatedUser);
    if (updateUser) {
      updateUser(updatedUser);
    }
  };

  const cancelSubscription = async (widgetId) => {
    setSubscriptions((subscriptions) =>
      subscriptions.filter((sub) => sub._id !== widgetId)
    );
  };

  const handleDeletePaymentMethod = async (paymentMethodId) => {
    try {
        await apiInstance.delete("/paymentMethod/" + paymentMethodId);
        setPaymentMethods((prevMethods) =>
            prevMethods.filter((method) => method._id !== paymentMethodId)
        );
    } catch(error) {
        console.error("Error deleting payment method:", error.message);
    }
  };

  const formatPrice = (amount) => {
    return `$${amount?.toFixed(2) || "0.00"}/month`;
  };

  return (
    <div className="mjwi-subscription-details-container">
      <button className="mjwi-back-button" onClick={closeUserDetail}>
        &larr; Back to Users
      </button>
      <h2>User Details</h2>
      <div className="mjwi-details-sections">
        <div className="mjwi-details-section span-2">
          <span className="mjwidev-id-info" title="User ID">{userData.id}</span>
          {
            userData.stripeCustomerId && 
            <span className="mjwidev-id-info" title="Stripe Customer ID">{userData.stripeCustomerId}</span>
          }
          <h3 className="mjwi-section-title">User Information</h3>
          <p className="mjwi-detail-item">
            <strong className="mjwi-detail-label">Name:</strong>
            <span className="mjwi-detail-value">{userData.displayName}</span>
          </p>
          <p className="mjwi-detail-item">
            <strong className="mjwi-detail-label">Email:</strong>
            <span className="mjwi-detail-value">{userData.email}</span>
          </p>
          <p className="mjwi-detail-item">
            <strong className="mjwi-detail-label">Role:</strong>
            <span className="mjwi-detail-value">
              {userData.userRole || "customer"}
            </span>
          </p>
          <p className="mjwi-detail-item">
            <strong className="mjwi-detail-label">Registered on:</strong>
            <span className="mjwi-detail-value">
              {formatDate(userData.createdAt)}
            </span>
          </p>
          <p className="mjwi-detail-item">
            <strong className="mjwi-detail-label">Email Verification:</strong>
            <span style={{
              display : "flex",
              gap : "4px",
            }} className="mjwi-detail-value">
              {messagesShowing && <div style={{color : "green"}} >Email sent successfully!</div>}
              {userData.emailVerified ? (
                <i
                  title="Email verified"
                  className="fa-solid fa-circle-check"
                  style={{ color: "green" }}
                ></i>
              ) : (
                <i
                  onClick={() => handleEmailVerification(userData.email)}
                  title="Email not verified"
                  className="fa-solid fa-circle-exclamation"
                  style={{ color: "red", cursor: "pointer" }}
                ></i>
              )}
            </span>
          </p>
          {!userData.emailVerified && (
            <Button
              name="Send Verification Mail"
              eventHandler={()=>handleEmailVerification(userData.email)}
            />
          )}
          <Button name="Edit" eventHandler={handleEditUser} />
          <Button name="Delete" eventHandler={handleDeleteUser} />
          <Button
            name="Switch User"
            eventHandler={() => switchUser(userData.id)}
          />
        </div>

        <h2>Payment Methods</h2>
        <div className="mjwi-details-section span-2">
            {paymentMethods.length > 0 ? (
                paymentMethods.map((item, index) => (
                    <div className="mjwi-payment-method" key={index}>
                        <span>{item?.paymentMethodType || "-"}</span>
                        <span>{item?.card?.brand || ""}</span>
                        <span>{item?.card?.last4 ? "****" + item?.card?.last4 : ""}</span>
                        <span>{item?.card?.country || ""}</span>
                        <span>Expires: {`${item?.card?.exp_month || ""} / ${item?.card?.exp_year || ""}`}</span>
                        <i className="fa-solid fa-trash" onClick={(e) => handleDeletePaymentMethod(item._id)}></i>
                    </div>
                ))
            ) : ("No Payment Methods")}
        </div>

        <h2>Subscriptions</h2>
        <div className="mjwi-details-section span-2">
          <SubscriptionsTable
            maxHeight="auto"
            subscriptions={subscriptions}
            setSubscriptions={setSubscriptions}
            selectedSubscription={selectedSubscription}
            setSelectedSubscription={setSelectedSubscription}
            formatDate={formatDate}
            formatPrice={formatPrice}
            cancelSubscription={cancelSubscription}
            loading={loading}
          />
        </div>
        <h2>Payment History</h2>
        <div className="mjwi-details-section span-2">
          <PaymentTable payments={payments} styleSubscription={"auto"} />
        </div>
      </div>
      <PopupModal
        ref={popupModalRef}
        updateUser={handleUserUpdate}
        deleteUser={deleteUserMain}
      />
    </div>
  );
};

export default UserDetails;
